import {
  copyArrayBuffer,
  copyArrayLoose,
  copyArrayStrict,
  copyBlob,
  copyDataView,
  copyDate,
  copyMapLoose,
  copyMapStrict,
  copyObjectLoose,
  copyObjectStrict,
  copyPrimitiveWrapper,
  copyRegExp,
  copySelf,
  copySetLoose,
  copySetStrict,
} from './copier';
import { createCache, getTag, MaxDepthExceededError } from './utils';

import type { InternalCopier, State } from './copier';

export type { State } from './copier';
export { MaxDepthExceededError } from './utils';

const { isArray } = Array;
const { assign } = Object;
// Handling extremely old environments without Object.getPrototypeOf.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const getPrototypeOf = Object.getPrototypeOf || ((obj) => obj.__proto__);

export interface CreateCopierOptions {
  array?: InternalCopier<any[]>;
  arrayBuffer?: InternalCopier<ArrayBuffer>;
  blob?: InternalCopier<Blob>;
  dataView?: InternalCopier<DataView>;
  date?: InternalCopier<Date>;
  error?: InternalCopier<any>;
  map?: InternalCopier<Map<any, any>>;
  object?: InternalCopier<Record<string, any>>;
  regExp?: InternalCopier<RegExp>;
  set?: InternalCopier<Set<any>>;
  /**
   * The maximum number of nested objects to traverse before throwing a
   * `MaxDepthExceededError`. Pass `Infinity` to traverse without a limit.
   *
   * @default 1000
   */
  maxDepth?: number;
}

/**
 * The maximum depth traversed when none is provided.
 *
 * @note
 * This is deliberately below the depth at which the native call stack is exhausted, so that
 * untrusted, deeply-nested values fail with a catchable, descriptive error instead of a raw
 * `RangeError`.
 */
const DEFAULT_MAX_DEPTH = 1000;

const DEFAULT_LOOSE_OPTIONS: Required<CreateCopierOptions> = {
  array: copyArrayLoose,
  arrayBuffer: copyArrayBuffer,
  blob: copyBlob,
  dataView: copyDataView,
  date: copyDate,
  error: copySelf,
  map: copyMapLoose,
  object: copyObjectLoose,
  regExp: copyRegExp,
  set: copySetLoose,
  maxDepth: DEFAULT_MAX_DEPTH,
};
const DEFAULT_STRICT_OPTIONS: Required<CreateCopierOptions> = assign(
  {},
  DEFAULT_LOOSE_OPTIONS,
  {
    array: copyArrayStrict,
    map: copyMapStrict,
    object: copyObjectStrict,
    set: copySetStrict,
  },
);

/**
 * Get the copiers used for each specific object tag.
 */
function getTagSpecificCopiers(
  options: Required<CreateCopierOptions>,
): Record<string, InternalCopier<any> | undefined> {
  return {
    Arguments: options.object,
    Array: options.array,
    ArrayBuffer: options.arrayBuffer,
    Blob: options.blob,
    Boolean: copyPrimitiveWrapper,
    DataView: options.dataView,
    Date: options.date,
    Error: options.error,
    Float32Array: options.arrayBuffer,
    Float64Array: options.arrayBuffer,
    Int8Array: options.arrayBuffer,
    Int16Array: options.arrayBuffer,
    Int32Array: options.arrayBuffer,
    Map: options.map,
    Number: copyPrimitiveWrapper,
    Object: options.object,
    Promise: copySelf,
    RegExp: options.regExp,
    Set: options.set,
    String: copyPrimitiveWrapper,
    WeakMap: copySelf,
    WeakSet: copySelf,
    Uint8Array: options.arrayBuffer,
    Uint8ClampedArray: options.arrayBuffer,
    Uint16Array: options.arrayBuffer,
    Uint32Array: options.arrayBuffer,
    Uint64Array: options.arrayBuffer,
  };
}

/**
 * Create a custom copier based on the object-specific copy methods passed.
 */
export function createCopier(options: CreateCopierOptions) {
  const normalizedOptions = assign({}, DEFAULT_LOOSE_OPTIONS, options);
  const tagSpecificCopiers = getTagSpecificCopiers(normalizedOptions);

  if (!tagSpecificCopiers.Object || !tagSpecificCopiers.Array) {
    throw new Error('An object and array copier must be provided.');
  }

  const { Array: copyArray, Object: copyObject } = tagSpecificCopiers;
  const { maxDepth } = normalizedOptions;

  function copier(value: any, state: State): any {
    state.prototype = state.Constructor = undefined;

    if (!value || typeof value !== 'object') {
      return value;
    }

    if (state.cache.has(value)) {
      return state.cache.get(value);
    }

    // Bound the traversal of nested objects, so that values nested more deeply than the
    // call stack can handle fail with a descriptive error instead of a raw `RangeError`.
    if (++state.depth > maxDepth) {
      throw new MaxDepthExceededError(maxDepth);
    }

    state.prototype = getPrototypeOf(value);
    // Using logical AND for speed, since optional chaining transforms to
    // a local variable usage.
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    state.Constructor = state.prototype && state.prototype.constructor;

    let clone: any;

    // plain objects
    if (!state.Constructor || state.Constructor === Object) {
      clone = copyObject(value, state);
    } else if (isArray(value)) {
      // arrays
      clone = copyArray(value, state);
    } else {
      const tagSpecificCopier = tagSpecificCopiers[getTag(value)];

      if (tagSpecificCopier) {
        clone = tagSpecificCopier(value, state);
      } else {
        clone =
          typeof value.then === 'function' ? value : copyObject(value, state);
      }
    }

    --state.depth;

    return clone;
  }

  return function copy<Value>(value: Value): Value {
    return copier(value, {
      Constructor: undefined,
      cache: createCache(),
      copier,
      depth: 0,
      prototype: undefined,
    });
  };
}

/**
 * Create a custom copier based on the object-specific copy methods passed, defaulting to the
 * same internals as `copyStrict`.
 */
export function createStrictCopier(options: CreateCopierOptions) {
  return createCopier(assign({}, DEFAULT_STRICT_OPTIONS, options));
}

/**
 * Copy an value deeply as much as possible, where strict recreation of object properties
 * are maintained. All properties (including non-enumerable ones) are copied with their
 * original property descriptors on both objects and arrays.
 */
export const copyStrict = createStrictCopier({});

/**
 * Copy an value deeply as much as possible.
 */
export default createCopier({});
